// Fuente unica de verdad del editor TipTap. Compartido entre:
//   - manage--tiptap-editor       → contenedor inline tipo textarea (sidebar form)
//   - showcase--inline-tiptap-editor → contenedor flotante (iframe preview del editor)
//
// Lo unico que cambia entre ambos es el LAYOUT del toolbar (CSS classes,
// posicionamiento) — todo el catalogo de items, los comandos, los checks
// de estado activo, y la carga lazy de TipTap son identicos.
//
// Cada controller importa `TOOLBAR_ITEMS` (que items existen, que icono,
// que comando, que tooltip) y los helpers (`execTiptapCommand`,
// `isTiptapItemActive`, `loadTiptapModules`, `createTiptapEditor`,
// `handleLinkPrompt`) — y construye su propio DOM con sus propias clases.

// --- Catalogo de items del toolbar ---
//
// Tipos de `command`:
//   - String standard: corresponde a un metodo de TipTap (`toggleBold`, etc.)
//   - String con prefijo `_`: handler custom — el controller debe proveerlo
//     en `customHandlers` al llamar `execTiptapCommand`.
//
// Tipos de check de active:
//   - `active`: nombre del mark/node a chequear con `editor.isActive(name)`
//   - `activeCheck`: objeto pasado a `editor.isActive(name, attrs)` (eg textAlign)
//
// `args`: argumento opcional para el comando (eg "left" para setTextAlign).

export const TOOLBAR_ITEMS = {
  paragraph:    { icon: "fa-solid fa-paragraph",     command: "setParagraph",        active: "paragraph",   tooltip: "Párrafo" },
  bold:         { icon: "fa-solid fa-bold",           command: "toggleBold",          active: "bold",        tooltip: "Negrita" },
  italic:       { icon: "fa-solid fa-italic",         command: "toggleItalic",        active: "italic",      tooltip: "Cursiva" },
  underline:    { icon: "fa-solid fa-underline",      command: "toggleUnderline",     active: "underline",   tooltip: "Subrayado" },
  strike:       { icon: "fa-solid fa-strikethrough",  command: "toggleStrike",        active: "strike",      tooltip: "Tachado" },
  bulletList:   { icon: "fa-solid fa-list-ul",        command: "toggleBulletList",    active: "bulletList",  tooltip: "Lista con viñetas" },
  orderedList:  { icon: "fa-solid fa-list-ol",        command: "toggleOrderedList",   active: "orderedList", tooltip: "Lista numerada" },
  left:         { icon: "fa-regular fa-align-left",   command: "setTextAlign",  args: "left",   activeCheck: { textAlign: "left" },   tooltip: "Alinear izquierda" },
  center:       { icon: "fa-regular fa-align-center", command: "setTextAlign",  args: "center", activeCheck: { textAlign: "center" }, tooltip: "Alinear centro" },
  right:        { icon: "fa-regular fa-align-right",  command: "setTextAlign",  args: "right",  activeCheck: { textAlign: "right" },  tooltip: "Alinear derecha" },
  link:         { icon: "fa-regular fa-link",         command: "_handleLink",          active: "link",        tooltip: "Enlace" },
  picture:      { icon: "fa-solid fa-image",          command: "_handleImage",                                tooltip: "Imagen" },
  "video-embed":  { icon: "fa-solid fa-video",        command: "_handleVideoEmbed",                           tooltip: "Video" },
  "button-link":  { icon: "fa-regular fa-link",       command: "_handleButtonTemplate", active: "buttonTemplate", tooltip: "Botón" },
  embed:        { icon: "fa-regular fa-code",         command: "_handleEmbed",         active: "embed",       tooltip: "Embed" }
}

export const HEADING_LEVELS = ["h1", "h2", "h3", "h4", "h5", "h6"]
export const HEADING_LEVEL_NUMBERS = [1, 2, 3, 4, 5, 6]

// Items del dropdown de encabezados — incluye "Párrafo" + h1..h6.
// Cada controller usa esta data para construir su menu visual (clases distintas).
export function getHeadingDropdownItems() {
  return [
    {
      label: "Párrafo",
      icon: TOOLBAR_ITEMS.paragraph.icon,
      level: null  // null = paragraph (no heading)
    },
    ...HEADING_LEVEL_NUMBERS.map(num => ({
      label: `Encabezado ${num}`,
      icon: `fa-solid fa-h${num}`,
      level: num
    }))
  ]
}

// Aplica el item del dropdown de encabezados. Usado por ambos controllers.
export function applyHeadingItem(editor, { level }) {
  if (level === null) {
    editor.chain().focus().setParagraph().run()
  } else {
    editor.chain().focus().toggleHeading({ level }).run()
  }
}

// --- Carga lazy de TipTap ---
//
// `withMediaExtensions`: true cuando el contenedor soporta picture/video-embed/
// button-link/embed (sidebar form). False para inline edit (no requiere uploads).

export async function loadTiptapModules({ withMediaExtensions = false } = {}) {
  const baseLoads = [
    import("@tiptap/core"),
    import("@tiptap/starter-kit"),
    import("@tiptap/extension-text-align"),
    import("@tiptap/extension-underline"),
    import("@tiptap/extension-text-style"),
    import("@tiptap/extension-color"),
    import("@tiptap/extension-highlight"),
    import("@tiptap/extension-placeholder"),
    import("@tiptap/extension-link")
  ]

  const mediaLoads = withMediaExtensions ? [
    import("../manage/tiptap/custom_image_extension"),
    import("../manage/tiptap/youtube_extension"),
    import("../manage/tiptap/vimeo_extension"),
    import("../manage/tiptap/button_template_extension"),
    import("../manage/tiptap/embed_extension")
  ] : []

  const loaded = await Promise.all([...baseLoads, ...mediaLoads])

  const [
    coreModule,
    { default: StarterKit },
    { default: TextAlign },
    { default: Underline },
    textStyleModule,
    { default: Color },
    { default: Highlight },
    { default: Placeholder },
    { default: Link },
    ...mediaModules
  ] = loaded

  const modules = {
    Editor: coreModule.Editor,
    StarterKit,
    TextAlign,
    Underline,
    TextStyle: textStyleModule.TextStyle,
    Color,
    Highlight,
    Placeholder,
    Link
  }

  if (withMediaExtensions) {
    modules.CustomImageExtension   = mediaModules[0].CustomImageExtension
    modules.YouTubeExtension       = mediaModules[1].YouTubeExtension
    modules.VimeoExtension         = mediaModules[2].VimeoExtension
    modules.ButtonTemplateExtension = mediaModules[3].ButtonTemplateExtension
    modules.EmbedExtension         = mediaModules[4].EmbedExtension
  }

  return modules
}

// Construye el array de extensiones segun los items disponibles.
export function buildTiptapExtensions({ modules, items, placeholder = "Escribe algo..." }) {
  const extensions = [
    modules.StarterKit,
    modules.TextStyle,
    modules.Underline,
    modules.Color,
    modules.Highlight.configure({ multicolor: true }),
    modules.Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === "heading") return "Titulo...."
        return placeholder
      }
    }),
    modules.TextAlign.configure({ types: ["heading", "paragraph"] })
  ]

  if (modules.CustomImageExtension) {
    extensions.push(modules.CustomImageExtension.configure({ HTMLAttributes: { class: "custom-image" } }))
  }

  if (items.includes("link") || items.includes("button-link")) {
    extensions.push(modules.Link.configure({ autolink: true, HTMLAttributes: { class: "button-link" } }))
  }

  if (items.includes("video-embed") && modules.YouTubeExtension && modules.VimeoExtension) {
    extensions.push(modules.YouTubeExtension)
    extensions.push(modules.VimeoExtension)
  }

  if (items.includes("button-link") && modules.ButtonTemplateExtension) {
    extensions.push(modules.ButtonTemplateExtension)
  }

  if (items.includes("embed") && modules.EmbedExtension) {
    extensions.push(modules.EmbedExtension)
  }

  return extensions
}

// Crea una instancia del Editor. Devuelve la instancia para que el caller
// la maneje (almacenar, attach listeners, destroy en disconnect).
export function createTiptapEditor({ modules, element, content, extensions, editorProps, onUpdate }) {
  return new modules.Editor({
    element,
    extensions,
    content,
    editorProps,
    onUpdate
  })
}

// --- Comandos y estado activo ---

const ALIGNMENT_ITEMS = ["left", "center", "right"]

// True si el nodo actualmente seleccionado en el editor es una imagen
// del CustomImageExtension.
export function isImageNodeSelected(editor) {
  const selection = editor.state?.selection
  return selection?.node?.type?.name === "custom-image"
}

// Ejecuta el comando asociado a `item`. Para comandos custom (con prefijo `_`)
// el controller debe pasar `customHandlers` con una funcion por handler key.
//
// Smart alignment: si el item es left/center/right Y el nodo seleccionado es
// una imagen, cambia el atributo `float` de la imagen en lugar de aplicar
// text-align al paragraph contenedor. Esto permite alinear imagenes
// directamente desde los botones del toolbar.
export function execTiptapCommand({ editor, item, customHandlers = {} }) {
  const def = TOOLBAR_ITEMS[item]
  if (!def) return

  // Smart alignment para imagenes. updateAttributes reemplaza el nodo en
  // ProseMirror — la seleccion anterior queda obsoleta. Re-seleccionamos
  // el nodo en su misma posicion (`.from`) con setNodeSelection para que
  // el outline amarillo se mantenga y el usuario pueda seguir cambiando
  // la alineacion sin tener que volver a clickear la imagen.
  if (ALIGNMENT_ITEMS.includes(item) && isImageNodeSelected(editor)) {
    const pos = editor.state.selection.from
    editor.chain()
      .focus()
      .updateAttributes("custom-image", { float: item })
      .setNodeSelection(pos)
      .run()
    return
  }

  // Custom handlers (link, image, video-embed, button-link, embed)
  if (typeof def.command === "string" && def.command.startsWith("_")) {
    const handler = customHandlers[def.command]
    if (handler) handler(editor)
    return
  }

  if (def.args !== undefined) {
    editor.chain().focus()[def.command](def.args).run()
  } else {
    editor.chain().focus()[def.command]().run()
  }
}

// Chequea si el item esta activo en el estado actual del editor.
// Usado para resaltar botones en el toolbar.
export function isTiptapItemActive(editor, item) {
  if (item === "heading") {
    return HEADING_LEVEL_NUMBERS.some(l => editor.isActive("heading", { level: l }))
  }

  // Smart alignment: si la imagen esta seleccionada, los botones
  // left/center/right se chequean contra su atributo `float`.
  if (ALIGNMENT_ITEMS.includes(item) && isImageNodeSelected(editor)) {
    return editor.getAttributes("custom-image").float === item
  }

  const def = TOOLBAR_ITEMS[item]
  if (!def) return false
  if (def.activeCheck) return editor.isActive(def.activeCheck)
  if (def.active) return editor.isActive(def.active)
  return false
}

// --- Handlers built-in compartidos ---

// Prompt para crear/editar/quitar un link. Usado por ambos controllers.
export function handleLinkPrompt(editor) {
  const previousUrl = editor.getAttributes("link").href
  const url = window.prompt("URL", previousUrl)
  if (url === null) return
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run()
    return
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
}

// File picker + upload de imagen. Despues del upload, la inserta como
// nodo `image` en el editor. `onLoadingChange(show)` permite que el caller
// muestre/oculte su propio spinner (las dos versiones — sidebar inline y
// iframe flotante — tienen estilos diferentes, asi que solo aceptamos un
// callback).
export function handleImagePrompt(editor, uploadUrl = "/image/create", onLoadingChange = null) {
  const input = document.createElement("input")
  input.type = "file"
  input.accept = "image/*"
  input.addEventListener("change", async (e) => {
    const file = e.target?.files?.[0]
    if (!file) return

    onLoadingChange?.(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const csrf = document.querySelector("meta[name='csrf-token']")
      const headers = {}
      if (csrf?.content) headers["X-CSRF-Token"] = csrf.content

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
        headers,
        credentials: "same-origin"
      })
      const data = await response.json()

      if (data.success && data.medium) {
        editor.chain().focus().setImage({ src: data.medium }).run()
      }
    } catch (err) {
      console.error("[tiptap] Image upload failed:", err)
    } finally {
      onLoadingChange?.(false)
    }
  })
  input.click()
}

// --- Agrupado de items para layout con separadores ---
//
// Mapa item → categoria. Items consecutivos de la misma categoria forman
// un grupo visual. El layout del toolbar inserta un separador entre grupos
// distintos. Usado por el contenedor flotante; el sidebar inline ignora
// los grupos (los items van todos seguidos al estilo wysiwyg clasico).

export const ITEM_GROUPS = {
  paragraph: "headings",
  h1: "headings", h2: "headings", h3: "headings", h4: "headings", h5: "headings", h6: "headings",
  bold: "format", italic: "format", underline: "format", strike: "format",
  bulletList: "lists", orderedList: "lists",
  left: "align", center: "align", right: "align",
  color: "extras", link: "extras", picture: "extras",
  "video-embed": "extras", "button-link": "extras", embed: "extras"
}

// Convierte una lista plana de items en un array de grupos. Items
// consecutivos en la misma categoria van juntos. Los headings (h1..h6)
// se colapsan en un solo item logico `{ kind: "heading_dropdown", levels: [1,2,3,...] }`.
//
// Ejemplo:
//   groupItemsForLayout(["h1","h2","h3","bold","italic","color","link","picture"])
//   → [
//       [{ kind: "heading_dropdown", levels: [1, 2, 3] }],
//       [{ kind: "btn", item: "bold" }, { kind: "btn", item: "italic" }],
//       [{ kind: "color" }, { kind: "btn", item: "link" }, { kind: "btn", item: "picture" }]
//     ]
export function groupItemsForLayout(items) {
  const groups = []
  let currentGroup = []
  let currentCategory = null

  const pushGroup = () => {
    if (currentGroup.length) groups.push(currentGroup)
    currentGroup = []
  }

  // Colapsar headings consecutivos en un solo dropdown
  let pendingHeadings = []

  const flushHeadings = () => {
    if (pendingHeadings.length) {
      currentGroup.push({ kind: "heading_dropdown", levels: pendingHeadings.slice() })
      pendingHeadings = []
    }
  }

  items.forEach((item) => {
    const cat = ITEM_GROUPS[item]
    const isHeading = cat === "headings" && item !== "paragraph"

    if (cat !== currentCategory) {
      flushHeadings()
      pushGroup()
      currentCategory = cat
    }

    if (isHeading) {
      const num = parseInt(item.replace("h", ""), 10)
      if (num >= 1 && num <= 6) pendingHeadings.push(num)
      return
    }

    if (item === "color") {
      currentGroup.push({ kind: "color" })
      return
    }

    currentGroup.push({ kind: "btn", item })
  })

  flushHeadings()
  pushGroup()

  return groups
}
